import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar, Plus, CheckCircle, Trash2, Clock, Sun, Moon, Coffee, History } from "lucide-react";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";
import { API } from "../App";

const BehavioralActivationPage = ({ user, logout }) => {
  const { t, locale } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activities, setActivities] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [newActivity, setNewActivity] = useState({ name: "", category: "", timeSlot: "morning" });

  const ACTIVITY_CATEGORIES = [
    { id: "social", label: t("activation.social"), icon: "\u{1F465}", color: "bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-300" },
    { id: "physical", label: t("activation.physical"), icon: "\u{1F3C3}", color: "bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-300" },
    { id: "creative", label: t("activation.creative"), icon: "\u{1F3A8}", color: "bg-purple-100 text-purple-600 dark:bg-purple-950 dark:text-purple-300" },
    { id: "self-care", label: t("activation.wellbeing"), icon: "\u{1F486}", color: "bg-pink-100 text-pink-600" },
    { id: "productive", label: t("activation.productive"), icon: "\u{1F4BC}", color: "bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-300" },
    { id: "relaxation", label: t("activation.relaxation"), icon: "\u{1F9D8}", color: "bg-teal-100 text-teal-600 dark:bg-teal-950 dark:text-teal-300" },
  ];

  const TIME_SLOTS = [
    { id: "morning", label: t("activation.morning"), icon: Sun },
    { id: "afternoon", label: t("activation.afternoon"), icon: Coffee },
    { id: "evening", label: t("activation.evening"), icon: Moon },
  ];

  const addActivity = () => {
    if (!newActivity.name.trim() || !newActivity.category) return;
    const activity = { id: Date.now(), ...newActivity, date: selectedDate.toISOString(), completed: false };
    setActivities([...activities, activity]);
    setNewActivity({ name: "", category: "", timeSlot: "morning" });
  };

  const toggleActivity = (id) => {
    setActivities(activities.map(a => a.id === id ? { ...a, completed: !a.completed } : a));
  };

  const deleteActivity = (id) => {
    setActivities(activities.filter(a => a.id !== id));
  };

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/behavioral-activation`, { withCredentials: true });
      setAllEntries(res.data);
      const allActs = res.data.flatMap(e => (e.activities || []).map(a => ({ ...a, _entryId: e.entry_id })));
      setActivities(allActs);
    } catch (e) { console.error(e); }
  };

  const saveDay = useCallback(async () => {
    const dayActs = activities.filter(a => new Date(a.date).toDateString() === selectedDate.toDateString());
    if (dayActs.length === 0) return;
    try {
      const res = await axios.post(`${API}/behavioral-activation`, {
        activities: dayActs,
        date: selectedDate.toISOString(),
      }, { withCredentials: true });
      toast.success(t("activation.saved"));
      if (user?.role === "patient") promptShare(res.data, "behavioral_activation");
    } catch (e) { console.error(e); }
  }, [activities, selectedDate, t]);

  const todayActivities = activities.filter(a => new Date(a.date).toDateString() === selectedDate.toDateString());
  const completedCount = todayActivities.filter(a => a.completed).length;
  const dateFnsLocale = locale === "fr" ? fr : enUS;

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="behavioral-activation-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("activation.pageTitle")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("activation.pageSubtitle")}</p>
          </div>
          {allEntries.length > 0 && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <History className="w-4 h-4" /> {allEntries.length} {t("activation.daysPlanned")}
            </span>
          )}
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.behavioral_activation} />

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="space-y-6">
            <Card>
              <CardContent className="p-4">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  locale={dateFnsLocale}
                  className="w-full"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  {t("activation.addActivity")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t("activation.activity")}</Label>
                  <Input placeholder={t("activation.activityPlaceholder")} value={newActivity.name} onChange={(e) => setNewActivity({ ...newActivity, name: e.target.value })} data-testid="activity-name-input" />
                </div>

                <div className="space-y-2">
                  <Label>{t("activation.category")}</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {ACTIVITY_CATEGORIES.map((cat) => (
                      <button key={cat.id} onClick={() => setNewActivity({ ...newActivity, category: cat.id })} className={`p-2 rounded-lg text-center text-sm transition-all border ${newActivity.category === cat.id ? "bg-primary text-white border-primary" : "bg-background border-border hover:bg-muted"}`} data-testid={`category-${cat.id}`}>
                        <span className="text-lg">{cat.icon}</span>
                        <p className="text-xs mt-1">{cat.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("activation.timeOfDay")}</Label>
                  <div className="flex gap-2">
                    {TIME_SLOTS.map((slot) => (
                      <button key={slot.id} onClick={() => setNewActivity({ ...newActivity, timeSlot: slot.id })} className={`flex-1 p-2 rounded-lg flex items-center justify-center gap-2 transition-all border ${newActivity.timeSlot === slot.id ? "bg-primary text-white border-primary" : "bg-background border-border hover:bg-muted"}`} data-testid={`time-${slot.id}`}>
                        <slot.icon className="w-4 h-4" />
                        <span className="text-sm">{slot.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <Button onClick={addActivity} disabled={!newActivity.name.trim() || !newActivity.category} className="w-full bg-primary hover:bg-primary/90" data-testid="add-activity-btn">
                  {t("activation.addActivity")}
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    {format(selectedDate, "EEEE d MMMM", { locale: dateFnsLocale })}
                  </CardTitle>
                  <span className="text-sm text-muted-foreground">
                    {t("activation.completedCount", { completed: completedCount, total: todayActivities.length })}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                {todayActivities.length > 0 ? (
                  <>
                    <div className="space-y-6">
                      {TIME_SLOTS.map((slot) => {
                        const slotActivities = todayActivities.filter(a => a.timeSlot === slot.id);
                        if (slotActivities.length === 0) return null;
                        return (
                          <div key={slot.id}>
                            <div className="flex items-center gap-2 mb-3">
                              <slot.icon className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium text-muted-foreground">{slot.label}</span>
                            </div>
                            <div className="space-y-2">
                              {slotActivities.map((activity) => {
                                const category = ACTIVITY_CATEGORIES.find(c => c.id === activity.category);
                                return (
                                  <div key={activity.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${activity.completed ? "bg-accent/5 border-accent/20" : "bg-background"}`}>
                                    <button onClick={() => toggleActivity(activity.id)} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${activity.completed ? "bg-accent border-accent text-white" : "border-muted-foreground/30 hover:border-accent"}`} data-testid={`toggle-activity-${activity.id}`}>
                                      {activity.completed && <CheckCircle className="w-4 h-4" />}
                                    </button>
                                    <div className="flex-1">
                                      <p className={`font-medium ${activity.completed ? "line-through text-muted-foreground" : ""}`}>{activity.name}</p>
                                      {category && <span className="text-xs text-muted-foreground">{category.icon} {category.label}</span>}
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => deleteActivity(activity.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-activity-${activity.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Button onClick={saveDay} className="w-full mt-4" data-testid="save-day-btn">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      {t("activation.saveDay")}
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t("activation.noActivities")}</p>
                    <p className="text-sm text-muted-foreground text-sm sm:text-base mt-1">{t("activation.addImproveHint")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default BehavioralActivationPage;
